import React, { createContext, useContext, useReducer, useMemo, useCallback, useState, useEffect } from 'react'
import { storyReducer, initialStoryState } from './storyReducer'

const StoryContext = createContext()

export function StoryProvider({ children, initialState = {}, characterLimits }) {
  const [storyState, dispatch] = useReducer(storyReducer, { ...initialStoryState, ...initialState })

  const [pendingEdits, setPendingEdits] = useState({
    storyId: null,
    title: '',
    description: '',
    isValidStory: false,
    isGalleryStory: false,
  })
  const [saveHandler, setSaveHandler] = useState(null)

  const [limits, setLimits] = useState(
    characterLimits || {
      maxTitleCount: 35,
      maxTextCount: 240,
      lineCap: 11,
    }
  )

  // Update limits when characterLimits prop changes (e.g., when FreeformTextPicker is resized)
  useEffect(() => {
    if (characterLimits) {
      setLimits(characterLimits)
    }
  }, [characterLimits])

  const setTitle = useCallback((title) => dispatch({ type: 'SET_TITLE', payload: title }), [])
  const setDescription = useCallback((description) => dispatch({ type: 'SET_DESCRIPTION', payload: description }), [])
  const setStoryId = useCallback((id) => dispatch({ type: 'SET_ID', payload: id }), [])

  const setTab = useCallback((tabIndex) => dispatch({ type: 'SET_TAB', payload: tabIndex }), [])
  const [originalGalleryStory, setOriginalGalleryStory] = useState(null)
  const setIsGalleryStory = useCallback(
    (isGalleryStory) => dispatch({ type: 'SELECT_GALLERY_STORY', payload: isGalleryStory }),
    []
  )
  const resetStory = useCallback(() => dispatch({ type: 'RESET' }), [])

  const commitEdits = useCallback(() => {
    setStoryId(pendingEdits.storyId) // THIS MAY NOT BE NEEDED
    setTitle(pendingEdits.title)
    setDescription(pendingEdits.description)
    setIsGalleryStory(pendingEdits.isGalleryStory)
  }, [
    setStoryId,
    pendingEdits.storyId,
    pendingEdits.title,
    pendingEdits.description,
    pendingEdits.isGalleryStory,
    setTitle,
    setDescription,
    setIsGalleryStory,
  ])

  const isStoryValid = useCallback(
    (title, text) => {
      return (
        title.length <= limits.maxTitleCount &&
        text.length <= limits.maxTextCount &&
        title.length > 0 &&
        text.length > 0
      )
    },
    [limits.maxTitleCount, limits.maxTextCount]
  )

  const hasStoryChanged = useCallback(() => {
    // If it's a gallery story, compare with original gallery story data
    if (pendingEdits?.isGalleryStory && originalGalleryStory) {
      return (
        originalGalleryStory.title !== pendingEdits?.title ||
        originalGalleryStory.description !== pendingEdits?.description
      )
    }

    // For non-gallery stories, compare with storyState
    return storyState?.title !== pendingEdits?.title || storyState?.description !== pendingEdits?.description
  }, [
    storyState?.title,
    storyState?.description,
    pendingEdits?.title,
    pendingEdits?.description,
    pendingEdits?.isGalleryStory,
    originalGalleryStory,
  ])

  // Add function to set original gallery story
  const setOriginalGalleryStoryData = useCallback((storyData) => {
    setOriginalGalleryStory(storyData)
  }, [])

  // Reset original gallery story when needed
  const resetOriginalGalleryStory = useCallback(() => {
    setOriginalGalleryStory(null)
  }, [])

  const contextValue = useMemo(
    () => ({
      storyState,
      setTitle,
      setDescription,
      setStoryId,
      setTab,
      setIsGalleryStory,
      setOriginalGalleryStoryData,
      resetOriginalGalleryStory,
      resetStory,
      pendingEdits,
      setPendingEdits,
      hasStoryChanged,
      isStoryValid,
      commitEdits,
      setSaveHandler,
      saveHandler,
      characterLimits: limits,
      setCharacterLimits: setLimits,
    }),
    [
      storyState,
      setTitle,
      setDescription,
      setStoryId,
      setTab,
      setIsGalleryStory,
      setOriginalGalleryStoryData,
      resetOriginalGalleryStory,
      resetStory,
      pendingEdits,
      hasStoryChanged,
      isStoryValid,
      commitEdits,
      saveHandler,
      limits,
    ]
  )

  return <StoryContext.Provider value={contextValue}>{children}</StoryContext.Provider>
}

export function useStoryManager() {
  const context = useContext(StoryContext)
  if (!context) {
    throw new Error('useStoryManager must be used within a StoryProvider')
  }
  return context
}
